import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Bar,
  Brush,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { MaskValor } from '../../../../../component';
import { IChartProps, StickyTooltip } from '../../../../../component/charts';
import ChartCard from '../../../../../component/charts/chartCard';
import { labelFormatterNumber } from '../../../../../shared/formatters/formatters';
import { SalesChartData, SalesHarvestChartData } from './types';

export const SalesECOMAP: React.FC = () => {
  const { chartsDataPrimary } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  const { requestStatus } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  return (
    <ChartCard
      data={chartsDataPrimary['sales']}
      Chart={SalesChart}
      cy={'Sales'}
      id={'sales'}
      requestStatus={requestStatus['sales']}
    />
  );
};

export const SalesECO: React.FC = () => {
  const { chartsDataPrimary } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  const { requestStatus } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  return (
    <ChartCard
      data={chartsDataPrimary['salesEcoMap']}
      Chart={SalesChart}
      cy={'SalesEco'}
      id={'salesEco'}
      requestStatus={requestStatus['salesEcoMap']}
    />
  );
};

export const SalesMAP: React.FC = () => {
  const { chartsDataPrimary } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  const { requestStatus } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  return (
    <ChartCard
      data={chartsDataPrimary['salesEcoMap']}
      Chart={SalesChart}
      cy={'SalesMap'}
      id={'salesMap'}
      requestStatus={requestStatus['salesEcoMap']}
    />
  );
};

const SalesChart: React.FC<IChartProps<SalesChartData[]>> = ({
  data,
  cy,
  id,
  timeframe,
  divisionValue = 1,
}) => {
  const [hasClicked, setHasClicked] = useState<boolean>(false);
  const [recordTooltip, setRecordTooltip] = useState<JSX.Element | null>(null);
  const [stopChange, setStopChange] = useState<boolean>(false);
  const formatterFunctions = [
    labelFormatterNumber,
    labelFormatterNumber,
    labelFormatterNumber,
    labelFormatterNumber,
  ];
  const datakeyType = (e: any) => {
    switch (e) {
      case 'Ano':
        return 'year';
      case 'Trimestre':
        return 'quarter';
      default:
        return 'month';
    }
  };
  const data100 = data.map((e: any) => {
    return {
      ...e,
      units_released_count: e.units_released_count / divisionValue,
      units_sold_count: e.units_sold_count / divisionValue,
    };
  });

  const renderQuarterTick = (tickProps: any) => {
    const { width, height, x, y, payload, visibleTicksCount } = tickProps;
    const { value, offset } = payload;
    const dataInfo = data100.filter((e) => e[datakeyType(timeframe)] === value);
    let xrect = x + 10;
    let xrectw = width / visibleTicksCount - 20;
    let xtext = x + width / visibleTicksCount / 2;

    if (xrectw < 40) {
      xrect = x;
      xrectw = width / visibleTicksCount;
      xtext = x + width / visibleTicksCount / 2;
    }

    return (
      <>
        <rect x={xrect} y={y - 53} width={xrectw} height="40" fill="#f1f1f1" />
        <text
          x={xtext}
          y={y - 35}
          width={width / visibleTicksCount}
          height={height}
          fill="#333"
          offset={offset}
          className="recharts-text recharts-label"
          textAnchor="middle"
        >
          <tspan>{dataInfo[0][idBar(id, 'vso')]}</tspan>
          <tspan x={xtext} y={y - 20} fontSize="12px">
            VSO
          </tspan>
        </text>
      </>
    );
  };
  const idBar = (e: any, t: string) => {
    switch (e) {
      case 'salesEcoChart':
        return `eco_${t}`;
      case 'salesMapChart':
        return `map_${t}`;
      default:
        return t;
    }
  };

  return (
    <ResponsiveContainer width="100%" height="100%" data-cy={`Sales${cy}`}>
      <ComposedChart
        id={id}
        data={data100}
        margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 5,
        }}
        onClick={() => setHasClicked(!hasClicked)}
      >
        <Tooltip
          wrapperStyle={{ visibility: 'visible' }}
          content={
            <StickyTooltip
              hasClicked={hasClicked}
              recordTooltip={recordTooltip}
              setRecordTooltip={setRecordTooltip}
              stopChange={stopChange}
              setStopChange={setStopChange}
              formatterFunctions={formatterFunctions}
            />
          }
        />
        <XAxis height={70} dataKey={datakeyType(timeframe)} />
        {divisionValue > 1 ? (
          <YAxis
            label={{
              value: `X ${divisionValue}`,
              angle: -90,
              position: 'insideLeft',
            }}
          />
        ) : null}
        <YAxis />
        <XAxis
          dataKey={datakeyType(timeframe)}
          axisLine={false}
          tickLine={false}
          interval={0}
          tick={renderQuarterTick}
          height={1}
          scale="band"
          xAxisId="quarter"
        />
        <Legend iconType="circle" />
        <Bar
          isAnimationActive={false}
          dataKey={idBar(id, 'units_released_count')}
          name="Unidades lançadas no período"
          fill="#ba96d9"
        >
          <LabelList
            dataKey={idBar(id, 'units_released_count')}
            position="top"
            formatter={(v: any) => MaskValor(v)}
          />
        </Bar>
        <Bar
          isAnimationActive={false}
          dataKey={idBar(id, 'units_sold_count')}
          name="Unidades vendidas de lançamentos no período"
          fill="#3f205a"
        >
          <LabelList
            dataKey={idBar(id, 'units_sold_count')}
            position="top"
            formatter={(v: any) => MaskValor(v)}
          />
        </Bar>
        <Line
          isAnimationActive={false}
          name="Unidades lançadas média"
          type="monotone"
          dataKey={idBar(id, `average_releases_${datakeyType(timeframe)}s`)}
          stroke="#c47500"
        />
        <Line
          isAnimationActive={false}
          name="Unidades vendidas média"
          type="monotone"
          dataKey={idBar(id, `average_sales_${datakeyType(timeframe)}s`)}
          stroke="#ff0000"
        />
        <Brush dataKey="period" height={30} stroke="#8884d8" />
      </ComposedChart>
    </ResponsiveContainer>
  );
};
